/**
 * The Temperatures screen (`/inventory/storage-temperatures`): which tab the
 * URL names, and the rows of its "Kept at options" tab. Pure so it is covered:
 * this repo's vitest only collects `src/**\/*.test.ts`.
 *
 * A "Kept at option" is an inventory storage profile seen from storage UI —
 * a name pointing at one temperature. Storage UI never calls it a profile.
 */

import type { ApiProfile, ProfileFieldEntry } from '@/types/inventory'
import type { StorageConditionMeta } from '@/types/storage'
import { STORAGE_CONDITION_FIELD, profileCondition } from '@/lib/inventoryProfiles'
import { keptAtOptions } from '@/lib/keptAt'

export const TEMPERATURES_TABS = ['temperatures', 'options', 'mapping'] as const
export type TemperaturesTab = (typeof TEMPERATURES_TABS)[number]

/** The tab `?tab=` names, or the first one for anything else. */
export function temperaturesTab(param: string | null): TemperaturesTab {
  return (TEMPERATURES_TABS as readonly string[]).includes(param ?? '') ? (param as TemperaturesTab) : 'temperatures'
}

/** One row of the "Kept at options" tab. */
export type KeptAtOptionRow = {
  profile: ApiProfile
  /** The temperature it points at; null when it names none, or one that no longer exists. */
  condition: StorageConditionMeta | null
  /** Offered in a unit's Kept-at menu — exactly what `keptAtOptions` lets through. */
  usable: boolean
  /** Why it is not offered, or null when it is. */
  reason: string | null
  /** Storage units whose Kept at is this option. */
  units: number
}

/**
 * Every storage profile as a Kept-at option row. Usable ones come first, in
 * the order the Kept-at menu lists them, so this tab reads like that menu; the
 * rest follow by name, each saying why it is not offered.
 */
export function keptAtOptionRows(
  profiles: ApiProfile[],
  conditions: StorageConditionMeta[],
  units: { storageProfileId: string | null }[],
): KeptAtOptionRow[] {
  const byName = new Map(conditions.map(c => [c.name, c]))
  const usage = new Map<string, number>()
  for (const u of units) if (u.storageProfileId) usage.set(u.storageProfileId, (usage.get(u.storageProfileId) ?? 0) + 1)

  const usableOrder = keptAtOptions(profiles, conditions).map(o => o.profileId)
  const rank = (p: ApiProfile) => {
    const i = usableOrder.indexOf(p.id)
    return i === -1 ? usableOrder.length : i
  }

  return profiles
    .map(profile => {
      const code = profileCondition(profile)
      const condition = (code != null && byName.get(code)) || null
      const usable = usableOrder.includes(profile.id)
      return {
        profile,
        condition,
        usable,
        reason: usable ? null : notUsableReason(condition),
        units: usage.get(profile.id) ?? 0,
      }
    })
    .sort((a, b) => rank(a.profile) - rank(b.profile) || a.profile.name.localeCompare(b.profile.name))
}

function notUsableReason(condition: StorageConditionMeta | null): string {
  if (!condition) return 'Not usable — no temperature'
  if (!condition.active) return 'Not usable — temperature is off'
  return 'Not usable — not for storage'
}

/**
 * True when some Kept at option points at this temperature. Decides what
 * adding a temperature reports: a server that creates the option alongside it
 * leaves nothing to do, one that does not leaves an option still to add.
 */
export function hasKeptAtOptionFor(profiles: ApiProfile[], conditionCode: string): boolean {
  return profiles.some(p => profileCondition(p) === conditionCode)
}

/** True when another option already has this name — the server refuses a duplicate per tenant. */
export function optionNameTaken(profiles: ApiProfile[], name: string, exceptId?: string): boolean {
  const wanted = name.trim().toLowerCase()
  return profiles.some(p => p.id !== exceptId && p.name.trim().toLowerCase() === wanted)
}

/**
 * The `fields` an option is saved with: its other fields as they are, and its
 * temperature pointed at `code`. A PATCH replaces `fields` whole, so an edit
 * that sent only the temperature would wipe the rest.
 */
export function keptAtOptionFields(
  fields: Record<string, ProfileFieldEntry> | undefined,
  code: string,
): Record<string, ProfileFieldEntry> {
  return { ...fields, [STORAGE_CONDITION_FIELD]: { active: true, value: code } }
}
